import React from 'react';
import I18n from 'i18n-js';
import moment from 'moment';

import { Listing } from '@reverbdotcom/commons/src/rql';
import { listingOrdersPath, listingOffersPath } from '@reverbdotcom/commons/src/url_helpers';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';

const DAYS_IN_MONTH = 30;
const DAYS_IN_YEAR = 365;
const DRAFT = 'draft';

interface IExternalProps {
  listing: Listing;
}

export function listingAge(publishedAtInSeconds: number) {
  const publishedAtInMilliseconds = publishedAtInSeconds * 1000;

  const today = moment().startOf('day');
  const publishedDate = moment(publishedAtInMilliseconds);

  const daysAgo = today.diff(publishedDate, 'days');

  if (daysAgo < 1) {
    return I18n.t('discovery.listingsManagement.sellerListingCard.daysAgo.lessThanOne');
  } else if (daysAgo < DAYS_IN_MONTH) {
    return I18n.t('discovery.listingsManagement.sellerListingCard.daysAgo', {
      count: daysAgo,
    });
  } else if (daysAgo < DAYS_IN_YEAR) {
    const monthsAgo = today.diff(publishedDate, 'months');

    return I18n.t('discovery.listingsManagement.sellerListingCard.monthsAgo', {
      count: monthsAgo,
    });
  } else {
    const yearsAgo = today.diff(publishedDate, 'years');

    return I18n.t('discovery.listingsManagement.sellerListingCard.yearsAgo', {
      count: yearsAgo,
    });
  }
}

export function ListingDetails({ listing }: IExternalProps) {
  return (
    <ul>
      {!!listing.sku && (
        <li>
          {I18n.t('discovery.listingsManagement.sellerListingCard.sku', {
            sku: listing.sku,
          })}
        </li>
      )}

      {listing.hasInventory && (
        <li>
          {I18n.t('discovery.listingsManagement.sellerListingCard.inStock', {
            inventory: listing.inventory,
          })}
        </li>
      )}

      {!!listing.condition?.displayName && (
        <li>
          {I18n.t('discovery.listingsManagement.sellerListingCard.condition', {
            condition: listing.condition.displayName,
          })}
        </li>
      )}

      {!!listing.publishedAt?.seconds && (
        <li>
          {listingAge(listing.publishedAt.seconds)}
        </li>
      )}
    </ul>
  );
}

export function ListingStats({ listing }: IExternalProps) {
  if (listing.state == DRAFT) return null;

  const ordersCount = listing.orderStats?.totalOrders ?? 0;
  const offersCount = listing.counts.offers;

  return (
    <ul className="d-flex fx-dir-col fx-align-end tablet-fx-align-start">
      <li>
        {I18n.t('discovery.listingsManagement.sellerListingCard.views', {
          count: listing.counts.views,
        })}
      </li>

      <li>
        {I18n.t('discovery.listingsManagement.sellerListingCard.watchers', {
          count: listing.counts.watchers,
        })}
      </li>

      {offersCount > 0 && (
        <li>
          <CoreLink
            to={listingOffersPath(listing)}
            className="td-underline color-primary"
          >
            {I18n.t('discovery.listingsManagement.sellerListingCard.offers', {
              count: offersCount,
            })}
          </CoreLink>
        </li>
      )}

      {ordersCount > 0 && (
        <li>
          <CoreLink
            to={listingOrdersPath(listing)}
            className="td-underline color-primary"
          >
            {I18n.t('discovery.listingsManagement.sellerListingCard.orders', {
              count: ordersCount,
            })}
          </CoreLink>
        </li>
      )}

      {!!listing.bumpRate?.rate && (
        <li>
          {I18n.t('discovery.listingsManagement.sellerListingCard.bumped', {
            percent: listing.bumpRate.rate,
          })}
        </li>
      )}

    </ul>
  );
}

export default function SellerListingCardDetails({ listing }: IExternalProps) {
  return (
    <div className="d-flex fx-justify-between weight-bold size-100 tablet-fx-dir-col tablet-fx-align-start">
      <ListingDetails listing={listing} />
      <ListingStats listing={listing} />
    </div>
  );
}
